import styled, { keyframes } from "styled-components";

// Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Main Container
export const Container = styled.div`
  min-height: 100vh;
  background: var(--background);
  animation: ${fadeIn} 0.5s ease-out;
`;

// Header Components
export const Header = styled.header`
  background: var(--surface);
  border-bottom: 2px solid var(--accent-light);
  padding: 15px 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: var(--shadow-soft);
  animation: ${slideIn} 0.6s ease-out;
  @media (max-width: 550px) {
    padding: 10px 0;
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  max-width: 1440px;
  padding: 0 20px;
`;

export const AdminLogo = styled.img`
  max-height: 45px;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 550px) {
    display: none;
  }
`;

export const AdminInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const AdminAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: var(--surface);
  font-weight: 700;
  border: 2px solid var(--accent-light);
`;

export const AdminDetails = styled.div`
  h4 {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
  }

  p {
    font-size: 11px;
    color: var(--text-muted);
    text-transform: uppercase;
  }
`;

export const LogoutBtn = styled.button`
  background: transparent;
  border: 2px solid var(--accent-light);
  color: var(--text-secondary);
  padding: 10px 12px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    transform: translateY(-2px);
  }
`;

export const Tooltip = styled.div`
  position: absolute;
  bottom: -35px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--text-primary);
  color: var(--surface);
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 11px;
  white-space: nowrap;
  opacity: ${(props) => (props.$show ? 1 : 0)};
  visibility: ${(props) => (props.$show ? "visible" : "hidden")};
  transition: all 0.3s ease;
  z-index: 1000;

  &::before {
    content: "";
    position: absolute;
    top: -4px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-bottom: 4px solid var(--text-primary);
  }
`;

// Layout Components
export const LayoutContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 20px;
`;

export const MainLayout = styled.div`
  display: flex;
  flex-direction: row;
  padding: 20px;
  gap: 30px;
  max-width: 1440px;
  margin: 0 auto;
  height: 100%;

  @media (max-width: 1200px) {
    flex-direction: column;
    gap: 20px;
  }
`;
// Sidebar Components
export const Sidebar = styled.aside`
  background: var(--surface);
  border-radius: 25px;
  padding: 25px;
  min-width: 300px;
  border: 2px solid var(--accent-light);
  box-shadow: var(--shadow-soft);
  height: fit-content;
  position: sticky;
  /* top: 110px; */
  animation: ${slideInFromLeft} 0.6s ease-out 0.4s both;
  transition: all 0.3s ease;

  &:hover {
    /* border-color: var(--accent); */
    background: var(--surface);
  }

  @media (max-width: 1200px) {
    position: static;
  }

  @media (max-width: 550px) {
    display: none;
  }
`;

export const SidebarSection = styled.div`
  margin-bottom: 30px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SidebarTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--text-primary);
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const SidebarNav = styled.ul`
  list-style: none;

  li {
    margin-bottom: 8px;
  }
`;

export const NavLink = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 15px;
  border-radius: 15px;
  text-decoration: none;
  color: ${(props) =>
    props.$active ? "var(--surface)" : "var(--text-secondary)"};
  font-size: 13px;
  transition: all 0.3s ease;
  border: 1px solid
    ${(props) => (props.$active ? "var(--accent)" : "var(--accent-light)")};
  font-weight: 500;
  cursor: pointer;
  background: ${(props) =>
    props.$active ? "var(--gradient-primary)" : "var(--surface)"};

  &:hover {
    background: var(--gradient-primary);
    color: var(--surface);
    border-color: var(--accent);
    transform: translateX(5px);
  }
`;

// Main Content
export const MainContent = styled.main`
  background: var(--surface);
  border-radius: 25px;
  padding: 40px;
  border: 2px solid var(--accent-light);
  box-shadow: var(--shadow-soft);

  flex: 1;
  height: 100%;
  animation: ${slideInFromRight} 0.6s ease-out 0.4s both;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

// Mobile Menu Components (matching Header MobileMenu design)
export const AdminMobileMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  z-index: 999;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

export const AdminMobileMenuContainer = styled.div`
  position: fixed;
  top: 72.8px;
  right: 0px;
  width: calc(100% - 40px);
  max-width: 350px;
  background: var(--surface);
  border-radius: 20px;
  border: 1px solid var(--accent-light);
  border-top: none;

  z-index: 1001;
  overflow: hidden;
  transform: ${(props) =>
    props.$isOpen ? "translateX(0) scale(1)" : "translateX(100%) scale(0.95)"};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: top right;
  border-top-right-radius: 0px;
  border-top-left-radius: 0px;
`;

export const AdminMobileMenuHeader = styled.div`
  padding: 20px 25px;
  /* background: var(--accent-light); */
  border-bottom: 1px solid var(--accent-light);
`;

export const AdminMobileUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const AdminMobileAvatar = styled.div`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: var(--gradient-primary);
  color: var(--surface);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  border: 2px solid var(--accent-light);
`;

export const AdminMobileDetails = styled.div`
  h4 {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    color: var(--text-primary);
  }

  p {
    font-size: 12px;
    margin: 0;
    color: var(--text-secondary);
    text-transform: uppercase;
    font-weight: 500;
  }
`;

export const AdminMobileNav = styled.nav`
  padding: 20px 0;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
`;

export const AdminMobileNavLink = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 25px;
  text-decoration: none;
  color: var(--accent);
  font-weight: ${(props) => (props.$active ? "700" : "500")};
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  background: ${(props) =>
    props.$active ? "var(--secondary)" : "transparent"};

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 60%;
    width: ${(props) => (props.$active ? "3px" : "0")};
    background: var(--gradient-primary);
    transition: width 0.2s ease;
    border-radius: 0 2px 2px 0;
  }

  &:hover {
    background: var(--accent-light);
    padding-left: 30px;
  }

  svg {
    font-size: 16px;
  }
`;

export const AdminMobileMenuDivider = styled.div`
  height: 1px;
  background: var(--accent-light);
  margin: 15px 20px;
`;

export const AdminMobileActions = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const AdminMobileLogoutBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 20px;
  background: var(--gradient-primary);
  border: none;
  border-radius: 25px;
  color: var(--surface);
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: var(--shadow-soft);
  font-family: inherit;

  svg {
    font-size: 16px;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-medium);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;
